import {
  COLUMN_MAPPING,
  DATE_COLUMNS,
  NUMERIC_COLUMNS,
  PROXIMITY_SORT_COLUMNS,
  SPECIAL_COLUMNS
} from '@/constants/eventColumns'

const getNestedValue = (obj, path) => {
  if (!path.includes('.')) return obj[path]

  const keys = path.split('.')
  return keys.reduce((acc, key) => acc?.[key], obj)
}

const sortByDateProximity = (aDate, bDate) => {
  const now = Date.now()
  const aDiff = Math.abs(aDate - now)
  const bDiff = Math.abs(bDate - now)
  const aIsFutureOrPresent = aDate >= now
  const bIsFutureOrPresent = bDate >= now

  if (aIsFutureOrPresent && !bIsFutureOrPresent) return -1
  if (!aIsFutureOrPresent && bIsFutureOrPresent) return 1

  return aDiff - bDiff
}

const compareDates = (aValue, bValue, column, order) => {
  const aDate = aValue ? new Date(aValue).getTime() : 0
  const bDate = bValue ? new Date(bValue).getTime() : 0

  if (PROXIMITY_SORT_COLUMNS.includes(column) && order === 'asc') {
    return sortByDateProximity(aDate, bDate)
  }

  return order === 'asc' ? aDate - bDate : bDate - aDate
}

const compareNumbers = (aValue, bValue, order) => {
  const aNum = Number.parseFloat(aValue) || 0
  const bNum = Number.parseFloat(bValue) || 0
  return order === 'asc' ? aNum - bNum : bNum - aNum
}

const compareStrings = (aValue, bValue, order) => {
  const aStr = String(aValue || '').toLowerCase()
  const bStr = String(bValue || '').toLowerCase()

  if (aStr < bStr) return order === 'asc' ? -1 : 1
  if (aStr > bStr) return order === 'asc' ? 1 : -1
  return 0
}

const getMappedColumn = (column) => COLUMN_MAPPING[column] || column

const compareItems = (a, b, sortConfig) => {
  const column = getMappedColumn(sortConfig.column)
  const { order } = sortConfig

  const aValue = getNestedValue(a, column)
  const bValue = getNestedValue(b, column)

  if (DATE_COLUMNS.includes(column)) {
    return compareDates(aValue, bValue, column, order)
  }

  if (NUMERIC_COLUMNS.includes(column)) {
    return compareNumbers(aValue, bValue, order)
  }

  return compareStrings(aValue, bValue, order)
}

/**
 * Sorts the events based on the provided sort configuration.
 * @param {Object} data - The data object containing benefitEvents.
 * @param {Object} sortConfig - The sort configuration with column and order.
 * @param {string} sortConfig.column - The column to sort by.
 * @param {string} sortConfig.order - The sort order ('asc' or 'desc').
 * @returns {Object} The data object with sorted items.
 */
export const sortEvents = (data, sortConfig) => {
  if (!data?.benefitEvents?.items || sortConfig.column === SPECIAL_COLUMNS.EDIT) {
    return data
  }

  const sortedItems = [...data.benefitEvents.items].sort((a, b) => compareItems(a, b, sortConfig))

  return {
    benefitEvents: {
      ...data.benefitEvents,
      items: sortedItems,
      size: sortedItems.length,
      total: sortedItems.length
    }
  }
}
